import React, { useMemo, useRef, useState } from 'react'
import { generatePath } from 'react-router-dom'

import ChevronRight from '@mui/icons-material/ChevronRight'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import PersonAdd from '@mui/icons-material/PersonAdd'
import {
	Chip,
	Divider,
	IconButton,
	Link,
	Menu,
	MenuItem,
	Typography,
} from '@mui/material'
import { Box } from '@mui/system'
import { GAME_TYPE } from 'constants/enums/GameTypeEnum'
import { ERRORS_PAGE, PUBLISHED_GAME_ROUTE_TEMPLATE } from 'constants/routes'
import { useBoolean } from 'usehooks-ts'
import { getRelativeDateTime } from 'utils/formatDate'

import { Game } from 'models/gameModels'

import { MODAL_TYPES, useModalContext } from 'contexts/ModalProvider'

import { styles } from './CustomGameCard.styles'

export const TEST_ID = 'custom-game-card'

export interface Props {
	game: Game
	handleDeleteRequest?: (gameId: string) => void
}

export default function CustomGameCard(props: Props) {
	const { showModal } = useModalContext()
	const {
		setTrue: openMenu,
		setFalse: closeMenu,
		value: isMenuOpen,
	} = useBoolean(false)

	const optionsMenuButtonRef = useRef<HTMLButtonElement>(null)

	const { game } = props
	const { backgroundGradient, label } = Object.values(GAME_TYPE).filter(
		(type) => type.value === Number(game.gameTypeId)
	)[0]

	const chips = [
		{ label: game.innovationCompany, background: '#00000014' },
		{ label, background: backgroundGradient },
	]
	const endTime = React.useCallback(
		() =>
			getRelativeDateTime(
				game.endTime ? new Date(game.endTime) : new Date()
			),
		[game.endTime]
	)
	const [remainingTime, setRemainingTime] = useState(endTime)

	// Efficiently update remainingTime when game expires
	React.useEffect(() => {
		const currentTime = endTime()

		// If already expired, no need for interval
		if (currentTime.isPast) {
			setRemainingTime(currentTime)
			return
		}

		// Calculate time until expiration
		const gameEndDate = game.endTime ? new Date(game.endTime) : new Date()
		const msUntilExpiration = gameEndDate.getTime() - Date.now()

		// If expiring soon (within 2 minutes), check every 10 seconds
		// Otherwise check every minute
		const checkInterval = msUntilExpiration < 2 * 60 * 1000 ? 10000 : 60000

		const interval = setInterval(() => {
			const updated = endTime()
			setRemainingTime((prev) => {
				// Only update if expiration status changed or time value changed
				if (
					prev.isPast !== updated.isPast ||
					prev.inWords !== updated.inWords
				) {
					return updated
				}
				return prev
			})

			// Clear interval once expired
			if (updated.isPast) {
				clearInterval(interval)
			}
		}, checkInterval)

		return () => clearInterval(interval)
	}, [game.endTime, endTime])

	const link = useMemo(() => {
		if (!game.gameId || remainingTime.isPast) {
			return generatePath(ERRORS_PAGE)
		}

		if (game.isAuthor || game.isParticipant) {
			return generatePath(PUBLISHED_GAME_ROUTE_TEMPLATE, {
				gameId: game.gameId,
			})
		}

		return generatePath(ERRORS_PAGE)
	}, [game.gameId, game.isAuthor, game.isParticipant, remainingTime.isPast])

	const handleShareClick = () => {
		showModal('SHARE_MODAL', {
			gameTitle: game.gameTitle,
			gameLink: link,
			gameId: game.gameId,
			expired: remainingTime.isPast,
		})
	}

	const handleEditClick = () => {
		showModal(MODAL_TYPES.CUSTOM_GAME_MODAL, {
			editable: true,
			gameId: game.gameId,
		})
	}

	const handleDeleteClick = () => {
		const { handleDeleteRequest } = props
		showModal(MODAL_TYPES.DELETE_PUBLISHED_GAME_MODAL, {
			handleDeleteClick: () => handleDeleteRequest?.(game.gameId),
			gameTitle: game.gameTitle,
		})
	}

	return (
		<Box data-testid={TEST_ID} sx={styles.container}>
			<Box
				sx={{ ...styles.gradientBar, background: backgroundGradient }}
			></Box>
			<Box sx={styles.topRow}>
				<Box>
					{chips.map((chip) => (
						<Chip
							label={chip.label}
							variant={'filled'}
							key={chip.label}
							data-testid={`${TEST_ID}-chip-${chip.label}`}
							sx={{
								...styles.chip,
								background: chip.background,
							}}
						/>
					))}
				</Box>
				{game.isAuthor && (
					<>
						<IconButton
							id="more-options-button"
							data-testid={`${TEST_ID}-more-options-button`}
							sx={styles.iconButton}
							ref={optionsMenuButtonRef}
							onClick={openMenu}
						>
							<MoreVertIcon />
						</IconButton>
						<Menu
							id="more-options-menu"
							data-testid={`${TEST_ID}-more-options-menu`}
							anchorEl={optionsMenuButtonRef.current}
							open={isMenuOpen}
							onClose={closeMenu}
							anchorOrigin={{
								vertical: 'bottom',
								horizontal: 'right',
							}}
							transformOrigin={{
								vertical: 'top',
								horizontal: 'right',
							}}
						>
							<MenuItem
								id="edit-game-button"
								data-testid={`${TEST_ID}-edit-game-button`}
								onClick={handleEditClick}
							>
								Edit Game
							</MenuItem>
							<MenuItem
								data-testid={`${TEST_ID}-delete-game-button`}
								id="delete-game-button"
								onClick={handleDeleteClick}
							>
								Delete Game
							</MenuItem>
						</Menu>
					</>
				)}
			</Box>
			<Link href={link} style={styles.link}>
				<Box sx={styles.middleRow}>
					<Typography
						data-testid={`${TEST_ID}-game-title`}
						sx={styles.title}
					>
						{game.gameTitle}
					</Typography>
					<ChevronRight sx={styles.chevron} />
				</Box>
			</Link>
			<Divider sx={styles.dividerStyle} />
			<Box sx={styles.bottomRow}>
				<Box sx={styles.stats}>
					<Box>
						<Typography>{game.ideaCount}</Typography>
						<Typography>{'Ideas'}</Typography>
					</Box>
					<Box>
						<Typography>{game.participantCount}</Typography>
						<Typography>{'Participants'}</Typography>
					</Box>
					<Box
						alignContent={'center'}
						style={styles.statsTypography.remainingTime}
					>
						{remainingTime.isPast ? (
							<Typography style={styles.statsTypography.expired}>
								{'GAME EXPIRED'}
							</Typography>
						) : (
							<React.Fragment>
								<Typography>
									{remainingTime.inWords.split(' ')[0]}
								</Typography>
								<Typography>
									{remainingTime.inWords.split(' ')[1] +
										' to go'}
								</Typography>
							</React.Fragment>
						)}
					</Box>
				</Box>
				<Box sx={styles.icons}>
					{game.isAuthor && (
						<>
							<IconButton
								data-testid={`${TEST_ID}-share-button`}
								sx={styles.shareButton}
								onClick={handleShareClick}
							>
								<PersonAdd />
							</IconButton>
						</>
					)}
				</Box>
			</Box>
		</Box>
	)
}
